import React, { useEffect, useRef, useState } from "react";
import { MdCancel, MdOutlineCloudUpload } from "react-icons/md";
import {
  useCreateMediaMutation,
  useGetMediaByIdQuery,
  useUpdateMediaMutation,
} from "../../redux/mediaApi";
import { useNavigate, useParams } from "react-router-dom";
import ButtonLoading from "../Button loading/ButtonLoading";
import { toast } from "react-toastify";
import { showErrorToast } from "../../utils/Utils";

const AddMedia = () => {
  const [title, setTitle] = useState("");
  const [videoURL, setVideoURL] = useState("");
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const [titleError, setTitleError] = useState("");
  const [videoURLError, setVideoURLError] = useState("");
  const [selectedOption, setSelectedOption] = useState("youtubeVideo");
  const [createMedia, { isLoading: isCreateLoading }] =
    useCreateMediaMutation();
  const navigate = useNavigate();
  const options = [
    { value: "youtubeVideo", label: "Youtube Video" },
    { value: "image", label: "Image" },
  ];

  const { id } = useParams();
  const isEditMode = Boolean(id);

  const { data, isLoading: isMediaLoading } = useGetMediaByIdQuery(id, {
    refetchOnFocus: false,
    skip: !isEditMode,
  });

  useEffect(() => {
    if (isEditMode && data) {
      setSelectedOption(data?.media?.mediaType);
      if (data?.media?.mediaType === "youtubeVideo") {
        setVideoURL(data?.media?.youtubeVideo);
      } else {
        setTitle(data?.media?.title);
        setImage({
          src: data?.media?.image?.imageUrl,
          file: null,
        });
      }
    }
  }, [isEditMode, data]);

  const [updateMedia, { isLoading: isUpdateLoading }] =
    useUpdateMediaMutation();

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    setError("");

    if (!file) {
      setError("No file selected.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Image size must be less than 10MB.");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      //   if (img.width !== 1610 || img.height !== 650) {
      //     setError("Image dimensions must be exactly 1610 x 650 pixels.");
      //     URL.revokeObjectURL(objectUrl);
      //     return;
      //   }

      const newImage = {
        src: objectUrl,
        file,
      };

      setImage(newImage);
    };

    img.onerror = () => {
      setError("Invalid image file.");
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDelete = () => {
    setImage(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    setTitleError("");
  };

  const handleVideoURLChange = (e) => {
    setVideoURL(e.target.value);
    setVideoURLError("");
  };

  const validateForm = () => {
    let isValid = true;

    if (selectedOption === "image") {
      if (!image) {
        setError("Please upload an image.");
        isValid = false;
      }
    } else {
      if (!videoURL) {
        setVideoURLError("Video URL is required");
        isValid = false;
      }
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    const mediaData = new FormData();
    if (selectedOption === "image") {
      mediaData.append("title", title);
      mediaData.append("mediaImage", image?.file);
    } else {
      mediaData.append("youtubeVideo", videoURL);
    }
    mediaData.append("mediaType", selectedOption);

    try {
      let response = null;
      if (isEditMode) {
        response = await updateMedia({ id, mediaData: mediaData }).unwrap();
      } else {
        response = await createMedia(mediaData).unwrap();
      }

      if (response.success) {
        handleCancel();
        toast.success(response.message);

        navigate("/media");
      }
    } catch (err) {
      console.error("media error:", err);
      showErrorToast(err);
    }
  };

  const handleCancel = () => {
    setTitle("");
    setTitleError("");
    setVideoURL("");
    setVideoURLError("");
    setImage(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  return (
    <div className="flex flex-col gap-3 bg-white rounded-xl xl:p-8 lg:p-6 p-4 ">
      <h1 className="text-[#05004E] lg:text-[1.8rem] md:text-[1.4rem] text-[1.2rem] font-semibold">
        {isEditMode ? "Update" : "Add"} Media
      </h1>
      {isMediaLoading ? (
        <div className="flex flex-col gap-4">
          <div className=" flex items-center gap-10">
            <div className=" flex items-center gap-4">
              <div className="h-6 w-6 bg-[#efefef] animate-pulse rounded-full"></div>
              <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
            </div>
            <div className=" flex items-center gap-4">
              <div className="h-6 w-6 bg-[#efefef] animate-pulse rounded-full"></div>
              <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
            <div className=" w-full h-12 rounded-xl bg-[#efefef] animate-pulse"></div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
            <div className=" w-full h-24 rounded-xl bg-[#efefef] animate-pulse"></div>
          </div>
          <div className="flex items-center justify-end gap-4">
            <div className="w-[10rem]  rounded-xl h-14 bg-[#efefef] animate-pulse"></div>
            <div className="w-[10rem]  rounded-xl h-14 bg-[#efefef] animate-pulse"></div>
          </div>
        </div>
      ) : (
        <form
          className="flex flex-col lg:gap-4 md:gap-3 gap-2 lg:text-[1rem] text-[.9rem]"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-wrap lg:gap-20 md:gap-10 gap-4">
            {options.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="radioGroup"
                  value={option.value}
                  checked={selectedOption === option.value}
                  onChange={() => setSelectedOption(option.value)}
                  className="accent-[#00643B]  md:w-5 md:h-5 w-4 h-4"
                />
                <span className=" font-semibold">{option.label}</span>
              </label>
            ))}
          </div>
          {selectedOption === "youtubeVideo" ? (
            <div className="flex flex-col md:gap-1 gap-[2px]">
              <label className="font-semibold">Video URL</label>
              <input
                type="text"
                placeholder="Enter url here"
                value={videoURL}
                onChange={handleVideoURLChange}
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
              {videoURLError && (
                <p className="text-red-500 md:text-sm text-[12px]">
                  {videoURLError}
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="flex flex-col md:gap-1 gap-[2px]">
                <label className="font-semibold">Title</label>
                <input
                  type="text"
                  placeholder="Title"
                  value={title}
                  onChange={handleTitleChange}
                  className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
                />
                {titleError && (
                  <p className="text-red-500 md:text-sm text-[12px] ">
                    {titleError}
                  </p>
                )}
              </div>
              <div className="flex flex-col md:gap-1 gap-[2px]">
                <label className="font-semibold">Upload Image</label>

                <div
                  className="w-full border border-[#00000033] md:p-4 p-3 rounded-xl "
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer  flex flex-col md:gap-2 gap-1 items-center w-full "
                  >
                    <MdOutlineCloudUpload className="text-[#00643b] lg:size-10 md:size-8 size-6" />
                    <p className="text-center font-semibold text-[#0B0B0B] ">
                      Drag your file(s) or{" "}
                      <span className=" text-[#00643b] text-center md:text-sm text-[12px] ">
                        browse
                      </span>
                    </p>

                    <p className="text-[#6D6D6D] text-center md:text-sm text-[12px] ">
                      Max 10 MB files are allowed
                    </p>
                  </label>

                  <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFile(e.target.files[0])}
                    className="hidden"
                    ref={fileInputRef}
                  />
                  {image && (
                    <div className="flex justify-center text-[#6D6D6D] items-center md:gap-4 gap-3 md:text-sm text-[12px] lg:py-[1.3rem] md:py-[1rem] py-[.5rem]">
                      <hr className="border-[#00000033] md:w-[22.22%] w-[30%] border-1" />
                      OR
                      <hr className="border-[#00000033] md:w-[22.22%] w-[30%] border-1" />
                    </div>
                  )}

                  {image && (
                    <div className="relative ">
                      <img
                        src={image.src}
                        alt="Upload Preview"
                        className=" object-cover md:rounded-[1rem] rounded-lg w-full "
                      />
                      <button
                        onClick={() => handleDelete()}
                        className="absolute md:-top-2.5 md:-right-2.5 -top-1.5 -right-1.5 bg-white rounded-full  "
                      >
                        <MdCancel className="lg:size-10 md:size-8 size-6 text-red-500" />
                      </button>
                    </div>
                  )}
                </div>
                {error && (
                  <p className="text-red-500 md:text-sm text-[12px] ">
                    {error}
                  </p>
                )}
              </div>
            </>
          )}

          <div className="flex items-center justify-end lg:gap-4 md:gap-3 gap-2">
            <p
              onClick={() => handleCancel()}
              className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border  border-[#00643B] text-[#00643B] text-center cursor-pointer  lg:rounded-xl md:rounded-lg rounded-md font-medium"
            >
              Cancel
            </p>
            <button
              type="submit"
              disabled={isCreateLoading || isUpdateLoading ? true : false}
              className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border border-[#00643B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white bg-[#00643B] lg:rounded-xl md:rounded-lg rounded-md font-medium"
            >
              {isEditMode ? (
                isUpdateLoading ? (
                  <ButtonLoading text="Update..." />
                ) : (
                  "Update"
                )
              ) : isCreateLoading ? (
                <ButtonLoading text="Add..." />
              ) : (
                "Add"
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddMedia;
